import type { SorobanToken } from 'types/token';

import { truncateString } from './truncate-string';

type SorobanTokenMetadataSource = Pick<SorobanToken, 'contract'> &
    Partial<Pick<SorobanToken, 'code' | 'name' | 'asset_name' | 'home_domain' | 'logo'>>;

export type SorobanTokenMetadata = {
    code: string;
    name: string;
    domain: string;
    domainUrl: string | null;
    logo: string | null;
};

const trim = (value?: string): string => value?.trim() ?? '';

const getDomainMetadata = (
    value: string,
): Pick<SorobanTokenMetadata, 'domain' | 'domainUrl'> | null => {
    if (!value || (/^[a-z][a-z\d+.-]*:/i.test(value) && !/^https?:\/\//i.test(value))) {
        return null;
    }

    try {
        const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);

        if (!['http:', 'https:'].includes(url.protocol) || !url.hostname) {
            return null;
        }

        return {
            domain: url.hostname.replace(/^www\./i, ''),
            domainUrl: url.toString(),
        };
    } catch {
        return null;
    }
};

export const getSorobanTokenMetadata = (
    token: SorobanTokenMetadataSource,
): SorobanTokenMetadata => {
    const fallback = truncateString(token.contract, 4);
    const code = trim(token.code) || fallback;
    const name = trim(token.asset_name) || trim(token.name) || code;
    const homeDomain = trim(token.home_domain);
    const domainMetadata = getDomainMetadata(homeDomain);

    return {
        code,
        name,
        // Without a usable home domain the contract id stands in, the way classic
        // assets fall back to a truncated issuer; the Soroban badge carries the
        // "this is a contract token" hint that a generic marker used to spell out.
        domain: domainMetadata?.domain ?? fallback,
        domainUrl: domainMetadata?.domainUrl ?? null,
        logo: trim(token.logo) || null,
    };
};
